import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router';
import cn from 'classnames';
import { forEach, isEmpty, orderBy, sortBy, reduce } from 'lodash';
import { Icon, LoadingSpinner } from 'legacy-packages/ft-cb';
import LearnosityProvider from 'legacy/src/utility/LearnosityProvider';
import { ASSIGNMENT_STATUSES } from 'legacy/src/constants/assignment';
import AssignItem from 'legacy-src/components/assessment/AssignItem';
import { getPriorStudentsSubdir } from 'legacy/src/utility/utils';
import log from 'chameleon/ui-stack/utilities/log';
import { UNITS as COPY } from 'chameleon/ui-stack/utilities/copy';
import { withPerformanceTier } from 'chameleon/src/hooks/usePerformanceTiers';
import { connect } from 'react-redux';
import { compose } from 'react-apollo';

const { oneOfType, arrayOf, shape, string, any, array, bool, object, number } =
  PropTypes;

/**
 * Provides the parts data for columns and items
 * */
export class PartsDataProvider extends PureComponent {
  static displayName = 'PartsDataProvider';

  /**
   * Stores the list of part reports by activity id
   * */
  cachedPartReports = {};

  /**
   * Stores statuses that have scores available
   * */
  statusesWithScores = [];

  static propTypes = {
    /**
     * React children
     * */
    children: any.isRequired,
    /**
     * Table th column info
     * */
    rawColumns: oneOfType([
      arrayOf(
        shape({
          /**
           * Column label
           * */
          label: string,
          /**
           * Column className
           * */
          className: string,
          /**
           * Children
           * */
          children: any,
        }),
      ),
      array,
    ]).isRequired,
    /**
     * Assignment has frq questions bool
     * */
    hasFrqQuestions: bool,
    /**
     * Current part label of the column
     * */
    currentPartLabel: string,
    /**
     * Current part activity
     * */
    currentPartActivityId: string,
    /**
     * Stringified response object for learnosity API (reports)
     * */
    resultByTags: string.isRequired,
    /**
     * Array of items for table body
     * */
    rawItems: array,
    /**
     * Other parts related to the current assessment
     * */
    otherPartsList: arrayOf(
      shape({
        assessmentId: number,
        assignmentId: number,
        hasBeenOpened: bool,
        learnosityActivityId: string,
        numberOfSubmissions: number,
        shortName: string,
        status: string,
        tagPairAbbreviations: array,
      }),
    ),
    /**
     * Router params provided by PerformanceTiersProvider - withRouter
     * */
    params: object.isRequired,
    /**
     * List of student taking this assignment (assigned to the class)
     * */
    students: array.isRequired,
    studentContext: shape({
      hasContext: bool,
      studentId: string,
      resultsPath: string,
      progressPath: string,
      questionsPath: string,
    }),
  };

  static defaultProps = {
    hasFrqQuestions: false,
    currentPartLabel: null,
    otherPartsList: [],
    rawItems: [],
    studentContext: {
      hasContext: false,
      studentId: null,
      resultsPath: '',
      progressPath: '',
      questionsPath: '',
    },
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      resultsData: [],
    };

    const {
      studentContext: { resultsPath, progressPath, questionsPath },
      params: { subject },
    } = props;
    const path = `/${subject}/assessments/`;
    this.assignmentsPath = path;

    let _resultsPath = resultsPath || `${path}results/:assignmentId`;
    this.resultsPath = (assignmentId) =>
      _resultsPath.replace(':assignmentId', assignmentId);
    const _progressPath =
      progressPath || `/${subject}/assignments/progress/:assignmentId`;
    this.progressPath = (assignmentId) =>
      _progressPath.replace(':assignmentId', assignmentId);
    const _questionsPath = questionsPath || `${path}results/:assignmentId`;
    this.questionsPath = (assignmentId, partId) =>
      _questionsPath
        .replace(':assignmentId', assignmentId)
        .replace(':partId', partId);

    const {
      userData: { isStudent },
      studentContext: { hasContext },
    } = props;
    this.hasStudentContext = isStudent || hasContext;

    const { assignmentProgressWithTagPairs } = props;
    const { attemptNumber = 0 } = assignmentProgressWithTagPairs;
    this.assignedIsReassigned = attemptNumber > 1;

    this.getCurrentPartColumnLabel = this.getCurrentPartColumnLabel.bind(this);
    this.updateColumnsWithPartColumns =
      this.updateColumnsWithPartColumns.bind(this);
    this.dataListener = this.dataListener.bind(this);
    this.getPartColumnUsers = this.getPartColumnUsers.bind(this);
    this.calculatePartColumnsData = this.calculatePartColumnsData.bind(this);
    this.calculatePointsByTags = this.calculatePointsByTags.bind(this);
    this.calculateUsersEarnedPoints =
      this.calculateUsersEarnedPoints.bind(this);
    this.getClassNameByAverage = this.getClassNameByAverage.bind(this);
    this.getReportByActivityId = this.getReportByActivityId.bind(this);
    this.getReportByActivityIdAllParts =
      this.getReportByActivityIdAllParts.bind(this);
    this.getReportsSignedRequest = this.getReportsSignedRequest.bind(this);
    this.calculateCellData = this.calculateCellData.bind(this);
    this.hasStudentScoresByActivityId =
      this.hasStudentScoresByActivityId.bind(this);
    this.cacheReportsByActivity = this.cacheReportsByActivity.bind(this);
    this.getStudentSession = this.getStudentSession.bind(this);
    this.cacheReportsByActivityData =
      this.cacheReportsByActivityData.bind(this);
  }

  componentDidMount() {
    this.cacheReportsByActivityData();
  }

  componentWillUnmount() {
    this.cachedPartReports = {};
    this.statusesWithScores = [];
  }

  cacheReportsByActivityData() {
    const { learnosityReportsByItemData, otherPartsResultsByItemData } =
      this.props;
    this.cacheReportsByActivity(learnosityReportsByItemData);
    this.cacheReportsByActivity(otherPartsResultsByItemData);
  }

  getCellLabel() {
    const { disablePpcAutoAssignFlag } = this.props;
    let label;
    if (disablePpcAutoAssignFlag && !this.assignedIsReassigned) {
      label = 'Not Assigned';
    } else {
      label = 'Not Reassigned';
    }

    return label;
  }

  /**
   * Get current part column label
   * @param {string} partLabel
   * */
  getCurrentPartColumnLabel(partLabel) {
    const { hasFrqQuestions } = this.props;
    /**
     * Default value for when no parts are available
     * */
    let columnPartLabel = 'Class Avg';
    if (partLabel && !hasFrqQuestions) {
      columnPartLabel = partLabel;
    }

    return columnPartLabel;
  }

  /**
   * Update columns with part columns
   * @param {array} rawColumns The raw columns array
   * @return {array} list of default columns + part columns
   * */
  updateColumnsWithPartColumns(rawColumns) {
    const {
      routes,
      userData: { isStudent, isAdmin, isCoordinator },
      params: { subject },
      otherPartsList,
      currentPartLabel,
      assignment: { id: currentPartAssignmentId },
      classes,
      onAssign,
    } = this.props;

    let columns = rawColumns;
    let partColumns = [];
    const currentPartColumnLabel =
      this.getCurrentPartColumnLabel(currentPartLabel);

    partColumns.push({
      isPartColumn: true,
      isCurrentPartColumn: true,
      label:
        this.hasStudentContext && otherPartsList.length < 1
          ? 'Your score'
          : currentPartColumnLabel,
      value: currentPartColumnLabel,
      mobile: {
        contentType: 'score',
      },
      className: 'Assignment--resources--parts is-current-part',
      children: this.hasStudentContext ? null : (
        <div className="Assignment--resources--parts-labels has-points order-2">
          <div>Average points</div>
          <div>earned/possible</div>
        </div>
      ),
    });

    otherPartsList.forEach((otherPart) => {
      const {
        shortName,
        assignmentId,
        itemData,
        status: assignmentStatus,
        hasBeenOpened,
        displayScore,
        studentSessions,
        numberOfSubmissions,
        unitId,
      } = otherPart;
      const { canScrambleQuestions, isSecure, itemId, title } = itemData[0];

      let actionColumnHeader;
      let columnHeaderStatus;
      let mobileActionColumnHeader;
      if (isStudent) {
        /**
         * Student only related headers
         * */
        const studentSession = this.getStudentSession(studentSessions);

        if (!assignmentId) {
          columnHeaderStatus = 'not-assigned';
          const label = this.getCellLabel();
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action">
              {label}
            </div>
          );
          mobileActionColumnHeader = <div>{label}</div>;
        } else if (
          displayScore &&
          studentSession &&
          studentSession.submittedAt &&
          !studentSession.scoringCompletedAt
        ) {
          columnHeaderStatus = 'refresh-for-results';
          const url = `/${subject}/assessments${getPriorStudentsSubdir(
            routes,
          )}/results/${currentPartAssignmentId}`;
          const link = <Link to={url}>Refresh for Results</Link>;
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action">
              {link}
            </div>
          );
          mobileActionColumnHeader = (
            <div className="status-part refresh-for-results">{link}</div>
          );
        } else if (
          !displayScore &&
          studentSession &&
          studentSession.submittedAt
        ) {
          columnHeaderStatus = 'awaiting-results';
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action with-no-results">
              Awaiting Results
            </div>
          );
          mobileActionColumnHeader = <div> Awaiting Results </div>;
        } else if (studentSession && studentSession.scoringCompletedAt) {
          columnHeaderStatus = 'view-part';
          this.statusesWithScores.push(columnHeaderStatus);
          const url = `/${subject}/assessments${getPriorStudentsSubdir(
            routes,
          )}/results/${assignmentId}`;
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action">
              <Link to={url}>View {shortName}</Link>
            </div>
          );
          mobileActionColumnHeader = (
            <div className="status-part view-results">
              <Link to={url}>View</Link>
            </div>
          );
        } else if (assignmentStatus === 'opened') {
          const label = studentSession ? 'Continue' : 'Begin';
          columnHeaderStatus = `take-quiz-${label.toLowerCase()}`;
          const url = `/${subject}/assessments/assignments/${assignmentId}`;
          const link = <Link to={url}>{label}</Link>;
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action">
              {link}
            </div>
          );
          mobileActionColumnHeader = (
            <div className={`status-part ${columnHeaderStatus}`}>{link}</div>
          );
        } else if (
          assignmentStatus === 'locked' ||
          assignmentStatus === 'closed'
        ) {
          columnHeaderStatus = 'locked';
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action">
              Locked
            </div>
          );
          mobileActionColumnHeader = (
            <div className="status-part is-locked with-label">
              <Icon name="locked" /> Locked
            </div>
          );
        }
      } else {
        /**
         * Teacher only related headers
         * */
        const isAdminOrCoord = isAdmin || isCoordinator;
        const disabledClass = isAdminOrCoord ? 'is-disabled' : '';

        if (!assignmentId) {
          columnHeaderStatus = 'not-assigned';
          const buttonProp = { button: 'Reassign' };
          const adminButtonProp = isAdminOrCoord ? buttonProp : {};
          const itemTitle = COPY.ASSIGN.MODAL_TITLE(
            unitId,
            `MCQ ${shortName}`,
            false,
            title,
          );

          const assignLink = (props) =>
            isAdminOrCoord && props.button ? (
              <span>
                {this.props.disablePpcAutoAssignFlag ? 'Assign' : 'Reassign'}
              </span>
            ) : (
              <AssignItem
                {...props}
                isPPC
                isAdminOrCoordinator={false}
                groups={classes}
                itemId={itemId}
                itemTitle={itemTitle}
                hideSecureToggle={!isSecure}
                onAssign={onAssign}
                scrambleable={canScrambleQuestions}
                notificationText=""
              />
            );

          actionColumnHeader = (
            <div
              className={`Assignment--resources--parts-labels has-action assign-link ${disabledClass}`}
            >
              {assignLink({ ...buttonProp })}
            </div>
          );
          mobileActionColumnHeader = (
            <div className={disabledClass}>
              {assignLink({ disabled: isAdminOrCoord, ...adminButtonProp })}
            </div>
          );
        } else if (numberOfSubmissions > 0) {
          columnHeaderStatus = 'view-part';
          this.statusesWithScores.push(columnHeaderStatus);
          const url = `/${subject}/assessments/results/${assignmentId}${getPriorStudentsSubdir(
            routes,
          )}`;
          actionColumnHeader = (
            <div className="Assignment--resources--parts-labels has-action">
              <Link to={url}>View {shortName} Results</Link>
            </div>
          );
          mobileActionColumnHeader = (
            <div className="status-part view-results">
              <Link to={url}>View</Link>
            </div>
          );
        } else if (hasBeenOpened) {
          columnHeaderStatus = 'view-progress';
          const url = this.progressPath(assignmentId);
          const text = 'View Progress';
          const link = isAdminOrCoord ? (
            <span>{text}</span>
          ) : (
            <Link to={url}>{text}</Link>
          );
          actionColumnHeader = (
            <div
              className={`Assignment--resources--parts-labels has-action ${disabledClass}`}
            >
              {link}
            </div>
          );
          mobileActionColumnHeader = (
            <div className={`status-part view-results ${disabledClass}`}>
              {link}
            </div>
          );
        } else if (assignmentStatus === 'locked' && !hasBeenOpened) {
          columnHeaderStatus = 'unlock';
          const url = this.progressPath(assignmentId);
          const text = 'Unlock';
          const link = isAdminOrCoord ? (
            <span>{text}</span>
          ) : (
            <Link to={url}>{text}</Link>
          );
          actionColumnHeader = (
            <div
              className={`Assignment--resources--parts-labels has-action ${disabledClass}`}
            >
              {link}
            </div>
          );
          mobileActionColumnHeader = (
            <div className={`status-part view-results ${disabledClass}`}>
              {link}
            </div>
          );
        }
      }
      partColumns.push({
        ...otherPart,
        isPartColumn: true,
        isOtherPartColumn: true,
        label: shortName,
        value: shortName,
        mobile: {
          contentType: 'score',
        },
        columnHeaderStatus,
        className: 'Assignment--resources--parts',
        children: actionColumnHeader,
        mobileActionColumnHeader,
      });
    });

    const orderedPartColumns = orderBy(partColumns, ['value', 'desc']) || [];

    return [...columns, ...orderedPartColumns];
  }

  /**
   * Handler callback for when data is available
   * @param {array} data The learnosity api response data
   * */
  dataListener(data) {
    this.setState({
      loading: false,
      resultsData: data,
    });
  }

  /**
   * Format assignment students by ordering them by first name and last name as a fallback and adds Learnosity reports
   * for those who took a submission.
   * @param {array} users                     - The list of users associated to a part column.
   * @param {array} partColumnItemReferences  - The list of part column item references.
   * @param {array} partQuestions             - The list of part questions with references
   * @param {string} activityId               - The activity ID
   * @returns {array}                         - A list of ordered students with reports property where exists.
   * */
  getPartColumnUsers(
    users,
    partColumnItemReferences,
    partQuestions,
    activityId,
  ) {
    const { students } = this.props;
    const partItemKeys = partQuestions
      .filter((question) =>
        partColumnItemReferences.includes(question.learnosityItemReference),
      )
      .map((question) => question.learnosityItemReference);

    const reportUserIds = users.map((user) => user.id);
    let orderedStudents = sortBy(students, ['firstName', 'lastName']);

    if (activityId) {
      const learnosityReport = this.getReportByActivityIdAllParts(activityId);
      if (!learnosityReport) {
        return [];
      }
      const report = learnosityReport.data[activityId];

      orderedStudents = orderedStudents.map((student) => {
        let reports = null;

        if (reportUserIds.includes(student.id)) {
          const userReport = report.users[student.id];
          if (userReport) {
            reports = partItemKeys.map((partItemKey) => {
              const item = userReport.items[partItemKey];
              const max_score = item && item.max_score;
              const score = item && item.score;
              return {
                itemKey: partItemKey,
                isAnswered: score !== null,
                isCorrect: max_score && score === max_score,
              };
            });
          }
        }

        return {
          ...student,
          reports,
        };
      });
    }

    return orderedStudents;
  }

  /**
   * Calculate part columns data
   * @param {array} rawResultsData The reports api response data
   * @param {array} columns
   * */
  calculatePartColumnsData(rawResultsData, columns) {
    const {
      rawItems,
      currentPartLabel,
      currentPartActivityId,
      resultByTags,
      assignment: { id: currentAssignmentId, questions: assignmentQuestions },
    } = this.props;
    const currentPartColumnLabel =
      this.getCurrentPartColumnLabel(currentPartLabel);
    const items = [];
    const otherPartsList = columns.filter(
      ({ isOtherPartColumn }) => isOtherPartColumn,
    );

    /**
     * Get the data from the first report
     * */
    const reportsSignedRequest = this.getReportsSignedRequest(resultByTags);
    const currentPartReportTags = this.getReportByActivityId(
      reportsSignedRequest,
      rawResultsData,
      currentPartActivityId,
    );
    const resultsDataByTags = this.calculatePointsByTags(
      currentPartReportTags.tags || [],
    );

    const otherPartsResultsDataByTags = {};
    otherPartsList.forEach((part) => {
      const { shortName, learnosityActivityId } = part;
      const otherPartsReportTags = this.getReportByActivityId(
        reportsSignedRequest,
        rawResultsData,
        learnosityActivityId,
      );
      if (!isEmpty(otherPartsReportTags)) {
        otherPartsResultsDataByTags[shortName] = this.calculatePointsByTags(
          otherPartsReportTags.tags || [],
        );
      }
    });

    rawItems.forEach((item) => {
      const { abbreviation } = item;
      const { label, order, average, users, questions } =
        this.calculateCellData(
          resultsDataByTags[abbreviation] || {},
          assignmentQuestions,
          currentPartActivityId,
        );
      let hasAverage = false;
      let hasStatus = false;
      let hasUniqueStatus = false;
      let uniqueData = null;
      /**
       * value used to determine whether or not show a particular Topic and Skill pair
       * in the Topic and Skills report.
       * A value >= 1 signifies one or more cells with a value we want the user to see
       * when value == 0, there's no information to show the user and we should therefore not show the row
       */
      let partsWithValues = 0;

      if (average) {
        hasAverage = true;
        partsWithValues++;
      }

      const newItem = {
        ...item,
        abbreviation,
        [currentPartColumnLabel]: {
          hasAverage,
          label,
          order,
          average,
          resultsPathname: `${this.questionsPath(
            currentAssignmentId,
            currentAssignmentId,
          )}`,
          className: cn(
            average !== null && this.getClassNameByAverage(average * 100),
            'Assignment--resources--parts-cell',
            'is-current-part',
          ),
          users,
          questions,
        },
      };

      otherPartsList.forEach((partList) => {
        const {
          assignmentId,
          learnosityActivityId,
          status: assignmentStatus,
          tagPairAbbreviations,
          shortName,
          questions: partQuestions,
          columnHeaderStatus,
        } = partList;

        newItem[shortName] = {
          className: 'Assignment--resources--parts-cell',
        };

        if (
          assignmentStatus &&
          tagPairAbbreviations.indexOf(abbreviation) > -1
        ) {
          if (
            this.statusesWithScores.includes(columnHeaderStatus) &&
            !isEmpty(otherPartsResultsDataByTags[shortName])
          ) {
            const data = otherPartsResultsDataByTags[shortName][abbreviation];
            const cellData = this.calculateCellData(
              data,
              partQuestions,
              learnosityActivityId,
            );
            const { label, order, average, users, questions } = cellData;

            if (questions && users && average) {
              /**
               * If we have scores but assignment is locked suppress
               * the average points
               * */
              hasAverage = true;
              partsWithValues++;
              newItem[shortName] = {
                hasAverage,
                label,
                order,
                average,
                isOtherPart: true,
                resultsPathname: `${this.questionsPath(
                  assignmentId,
                  assignmentId,
                )}`,
                className: cn(
                  average !== null && this.getClassNameByAverage(average * 100),
                  'Assignment--resources--parts-cell',
                ),
                users,
                questions,
              };
            }
          } else {
            const {
              icon: cellIconName,
              label: cellLabel,
              cellClassName,
            } = ASSIGNMENT_STATUSES[columnHeaderStatus] || {};
            hasStatus = true;
            if (cellIconName || cellLabel) {
              partsWithValues++;

              let url;
              if (
                columnHeaderStatus === 'take-quiz-begin' ||
                columnHeaderStatus === 'take-quiz-continue'
              ) {
                url = `${this.assignmentsPath}assignments/${assignmentId}`;
              }

              if (columnHeaderStatus === 'refresh-for-results') {
                url = `${this.resultsPath(currentAssignmentId)}`;
              }

              newItem[shortName] = {
                url,
                hasStatus,
                cellStatus: columnHeaderStatus,
                className: `other-part-cell Assignment--resources--parts-cell ${cellClassName}`,
                label: cellLabel || (
                  <span>
                    <Icon name={cellIconName} />
                  </span>
                ),
              };
            } else {
              hasUniqueStatus = true;
              uniqueData = {
                partName: shortName,
                label: columnHeaderStatus,
                className: 'Assignment--resources--parts',
              };

              hasStatus = !!columnHeaderStatus;
              newItem[shortName] = {
                hasStatus,
                hasUniqueStatus,
                cellStatus: columnHeaderStatus,
              };
            }
          }
        }

        // when we have tag abbrevitions but no assignmentID, this part is not reassigned
        if (!assignmentId && tagPairAbbreviations.indexOf(abbreviation) > -1) {
          partsWithValues++;
          const label = this.getCellLabel();
          newItem[shortName] = {
            label,
            className: `other-part-cell Assignment--resources--parts-cell not-assigned`,
            cellStatus: 'not-assigned',
          };
        }
      });

      /**
       * Check parts for averages
       * */
      const partStrings = [
        currentPartColumnLabel,
        ...otherPartsList.map(({ shortName }) => shortName),
      ];

      let commonPartsIndex = 0;
      let partsWithAverages = [];
      partStrings.forEach((part) => {
        if (newItem[part].hasAverage) {
          commonPartsIndex++;

          if (newItem[part].isOtherPart) {
            const { resultsPathname } = newItem[part];
            partsWithAverages.push({
              resultsPathname,
              shortName: part,
              className: 'Assignment--resources--parts',
            });
          }
        }
      });
      /**
       * Has at least one common part with average
       * */
      const hasCommonAverageParts = commonPartsIndex > 1;

      newItem.partsWithAverages = !hasCommonAverageParts
        ? partsWithAverages
        : [];
      newItem.hasAverage = hasAverage;
      newItem.hasStatus = hasStatus;
      newItem.hasUniqueStatus = hasUniqueStatus;
      newItem.uniqueData = uniqueData;
      newItem.partsWithValues = partsWithValues;
      newItem.hasCommonAverageParts = hasCommonAverageParts;

      /**
       * Gather more details for unique box
       * */
      if (partsWithValues === 1) {
        forEach(newItem, (itemProps, propName) => {
          if (
            typeof itemProps === 'object' &&
            itemProps &&
            itemProps.hasStatus
          ) {
            newItem.foundPart = propName;
          }
        });
      }

      items.push(newItem);
    });

    return items;
  }

  /**
   * Calculate points per tags from all users
   * @param {array} resultsByTags Learnosity response results
   * @return {object} The results per tags object
   * */
  calculatePointsByTags(resultsByTags) {
    let results = {};
    resultsByTags.forEach((tagItem) => {
      const { name: itemAbbr, item_references, users } = tagItem;

      const possiblePoints = item_references.length;
      const earnedPoints = this.calculateUsersEarnedPoints(users);
      const userIds = Object.keys(users);

      results[itemAbbr] = {
        earnedPoints,
        possiblePoints,
        userIds,
        itemReferences: item_references,
      };
    });

    return results;
  }

  /**
   * Calculate earned points for all users
   * @param {object} users The list of users
   * @return {number} The earned points
   * */
  calculateUsersEarnedPoints(users) {
    const averageUserPoints = reduce(
      users,
      (accumulator, user) => {
        const { count_items_correct } = user;

        return accumulator + count_items_correct;
      },
      0,
    );

    return parseFloat(
      (averageUserPoints / Object.keys(users).length).toFixed(1),
    );
  }

  /**
   * Get className by classname
   * @param {float} average
   * @return {string} className
   * */
  getClassNameByAverage(average) {
    const { performanceTiers } = this.props;
    const tiers = performanceTiers;

    let className = 'AverageRow ';

    if (average <= tiers[0].topLimit) {
      className += tiers[0].color;
    } else if (average <= tiers[1].topLimit) {
      className += tiers[1].color;
    } else if (average <= tiers[2].topLimit) {
      className += tiers[2].color;
    } else if (average <= tiers[3].topLimit) {
      className += tiers[3].color;
    }

    return className;
  }

  /**
   * Get report response by activity id
   * @param {object} reportsSignedRequest The reports signed request
   * @param {array} rawResultsResponseData The reports response data
   * @param {string} activityId The activity id used in the signed request
   * for each report
   * @returns {array} report data - used to identify the API response report
   * */
  getReportByActivityId(
    reportsSignedRequest,
    rawResultsResponseData,
    activityId,
  ) {
    const { id: reportId } =
      reportsSignedRequest.request.reports.find(
        ({ activity_id }) => activity_id === activityId,
      ) || {};
    if (!reportId) {
      return [];
    }

    const report = rawResultsResponseData.find(({ id }) => reportId === id);
    return report.data;
  }

  /**
   * Get correct report from learnosityReports current part and other parts
   * @param {string} activityId Used to identify the report
   * */
  getReportByActivityIdAllParts(activityId) {
    return this.cachedPartReports[activityId];
  }

  /**
   * Get reports signed request
   * @param {string} resultByTags Stringifed value
   * @retuns {object} The JSON parsed value
   * */
  getReportsSignedRequest(resultByTags) {
    let reportsSignedRequest = {};
    try {
      reportsSignedRequest = JSON.parse(resultByTags);
    } catch (error) {
      log('error', error);
    }

    return reportsSignedRequest;
  }

  /**
   * Rounds a number to two decimal places
   * @param {float} average
   * @returns {string} returns the propper rounded average with 2 decimals.
   */

  roundAverage(average) {
    /* I'm using Math.round because of the following stackoverflow:
     * https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary
     * Number.EPSILON is used to avoid rounding errors like 0.005 to 0.00 */
    return (
      parseFloat(Math.round((average + Number.EPSILON) * 100)) / 100
    ).toFixed(3);
  }

  /**
   * Calculates cell data - points
   * @param {object} tagData
   * @param {array} partQuestions
   * @param {string} activityId For identifying the proper report
   * @returns {object} Data formatted for cell data
   * */
  calculateCellData(tagData = {}, partQuestions, activityId) {
    const { students } = this.props;
    const { earnedPoints, possiblePoints, userIds, itemReferences } = tagData;

    let label = '';
    let order = 0;
    let average = null;
    let users = null;
    let questions = null;

    if (possiblePoints) {
      this.cacheReportsByActivityData();
      const partColumnStudents = (students || []).filter((student) =>
        userIds.includes(student.id),
      );

      label = `${earnedPoints}/${possiblePoints}${
        this.hasStudentContext ? ' Points' : ''
      }`;

      order = earnedPoints / possiblePoints;
      average = this.roundAverage(earnedPoints / possiblePoints);
      users =
        this.getPartColumnUsers(
          partColumnStudents,
          itemReferences,
          partQuestions,
          activityId,
        ) || 0;
      questions = partQuestions
        .filter((question) =>
          itemReferences.includes(question.learnosityItemReference),
        )
        .map((question) => ({
          ...question,
          index: partQuestions.indexOf(question),
        }));
    }

    return {
      label,
      order,
      average,
      users,
      questions,
    };
  }

  /**
   * @deprecated
   * Checks if student has scores by activityId
   * @param {string} activityId
   * @returns {boolean}
   * */
  hasStudentScoresByActivityId(activityId) {
    return !!this.cachedPartReports[activityId];
  }

  /**
   * Cache reports by activity
   * @param {array} reportsRaw
   * */
  cacheReportsByActivity(reportsRaw) {
    (reportsRaw || []).forEach((report) => {
      const reportKeys = Object.keys(report.data);
      const activityId = reportKeys[0];

      this.cachedPartReports[activityId] = report;
    });
  }

  /**
   * Get logged in student session data
   * @param {array} studentSessions
   * @returns {object} The session data
   * */
  getStudentSession(studentSessions) {
    const {
      userData: {
        me: { initId },
      },
    } = this.props;
    const currentSession = studentSessions.find(
      ({ userId }) => userId === initId,
    );

    return currentSession;
  }

  render() {
    const { loading, resultsData } = this.state;
    const {
      children,
      resultByTags,
      rawColumns,
      userData: { isStudent },
      studentContext,
    } = this.props;

    if (loading) {
      const getReportsSignedRequest =
        this.getReportsSignedRequest(resultByTags);

      return (
        <Fragment>
          <LoadingSpinner />
          <LearnosityProvider
            containerElement="learnosity_report_results_by_tag"
            apiType="reports"
            initOptions={getReportsSignedRequest}
            dataListener={this.dataListener}
          />
        </Fragment>
      );
    }

    const columns = this.updateColumnsWithPartColumns(rawColumns);
    const otherProps = {
      columns,
      items: this.calculatePartColumnsData(resultsData, columns),
      isStudent,
      hasStudentContext: this.hasStudentContext,
      studentContext,
    };

    return React.Children.map(
      children,
      (child) => child && React.cloneElement(child, otherProps),
    );
  }
}

function mapStateToProps(state) {
  return {
    userData: state.userData,
    classes: state.classes.items,
    disablePpcAutoAssignFlag: state.featureFlag.disable_ppc_auto_assign,
    subjectId: state.subject.data.id,
  };
}

export default compose(
  connect(mapStateToProps),
  withRouter,
  withPerformanceTier,
)(PartsDataProvider);
